import { setRedirectionUrl } from 'src/utils/helpers';
import { ROUTES } from 'src/utils/constants/routes';

import webview from '@adapter/webview';
import { isEmpty } from '@groww-tech-pvt/ella';
import { AxiosRequestConfig } from 'axios';
import queryString from 'query-string';

import { getDeviceUUID, setDeviceUUID } from 'src/utils/storage';
import { STORAGE_TYPE, STORAGE_CONSTANTS } from 'src/utils/storage/constant';
import { clearAllCookiesWithParentDomain, clearSpecificCookiesWithParentDomain } from 'src/utils/storage/helper';
import { clearStorage } from '@groww-tech-pvt/web-storage';
import { getDomainConfig } from '../domainConfig';
import { Base64Encoder, NoPadding, Utf8Encoder, encryptAesBase64, sha256Hash } from '@groww-tech-pvt/encryption';

const WV = webview();

export const getPayloadData = (config: AxiosRequestConfig) => {
  let payloadData = '';

  if (isValidPayload(config.data)) {
    // Axios Interceptors error config.data returns stringified object --> https://github.com/axios/axios/issues/4994
    payloadData =
      typeof config.data === 'object'
        ? JSON.stringify(config.data)
        : config.data;
  }

  return payloadData;
};

export const getQueryString = (config: AxiosRequestConfig) => {
  let queryStr = '';

  if (isValidPayload(config.params)) {
    const paramString = queryString.stringify(config.params, {
      arrayFormat: 'comma',
      encode: false,
    }); // it will ignore if params value is undefined

    queryStr = paramString ? '?' + paramString : '';
  }

  return queryStr;
};

export const logoutUser = () => {
  const deviceUUID = getDeviceUUID();

  clearStorage(STORAGE_TYPE.LOCAL_COOKIE_STORAGE);

  // Clear all cookies from subdomains and parent domains
  // This ensures tokens are removed across all subdomains (e.g., 915.groww.in, subdomain.groww.in)
  clearAllCookiesWithParentDomain();

  // Also explicitly clear token cookies across all domains
  clearSpecificCookiesWithParentDomain([
    STORAGE_CONSTANTS.AUTHORIZATION,
    STORAGE_CONSTANTS.REFRESH_TOKEN,
    STORAGE_CONSTANTS.PIN_TOKEN,
    STORAGE_CONSTANTS.SOCKET_TOKEN,
    STORAGE_CONSTANTS.N_KEY_SEED,
  ]);

  setDeviceUUID(deviceUUID);

  setRedirectionUrl('', true);

  if (WV.isActive) {
    // const postObj = {
    //   action: WEBVIEW_EVENTS_ACTION_TYPES.SESSION_EXPIRED,
    //   params: null,
    // };

    // postWindowMessage(postObj);
  } else {
    const isAlreadyOnLoginPage =
      typeof window !== 'undefined' &&
      window.location.pathname.includes(ROUTES.LOGIN);

    if (!isAlreadyOnLoginPage) {
      window.location.href = window.location.origin + ROUTES.LOGIN;
    }
  }
};

/**
 * generateLoginChecksum
 *
 * @param stringToHash - The string to hash
 * @returns The hashed string using sha256
 *
 * @internal
 * ```
 */
function generateLoginChecksum(stringToHash: string) {
  return sha256Hash(stringToHash).toString();
}

//utitlity function
function convertXReqIdToDesiredFormat(xReqId: string) {
  // alphanumeric
  // 13 letter
  // 0 padding at last if needed

  let temp = sha256Hash(xReqId).toString();

  if (temp.length < 13) {
    temp += '0000000000000'; // 13 zeros
  }

  return temp.substring(0, 13);
}


/**
 * getActualToken
 *
 * @param url - The url of the api which will have the generated checksum in the Headers
 * @param data - The payload data for the api. In case of get request it will be null
 * @param xReqId - The random request id associated with every request
 * @returns The token with format =>
 * xReqID####sha256(url+payload)
 *
 * @internal
 * ```
 */
function getActualToken(url: string, data: any, xReqId: string): string {
  // URL should not contain domain and port ....
  let finalUrl = url;

  // if it's post request then append url and request body
  if (data) {
    finalUrl += data;
  }

  // generate SHA256 checksum now
  const result = generateLoginChecksum(finalUrl);

  return convertXReqIdToDesiredFormat(xReqId) + '###' + result;
}

//utitlity function to generate random string
function generateRandomString() {
  return Math.random().toString(36).substring(7);
}

/**
 * convertStringToByteArray
 *
 * @param stringToHash - The string to convert to byte array
 * @returns The byte array of the string
 *
 * @example
 * convertStringToByteArray("ab")
 * ==> [ 97, 98 ]
 * @internal
 * ```
 */
function convertStringToByteArray(str: string) {
  let bytes: number[] = [];

  for (let i = 0; i < str.length; ++i) {
    const code = str.charCodeAt(i);

    bytes = bytes.concat([code]);
  }

  return bytes;
}

//utitlity function
function convertByteArrToString(array: number[]) {
  let result = '';

  for (let i = 0; i < array.length; i++) {
    result += String.fromCharCode(array[i]);
  }

  return result;
}

/**
 * generateKey
 *
 * @remarks Utility function to create key using salt and some random string
 *
 * @param salt - The salt that will be used to generate the key
 * @param randomString - The salt that will be used to generate the key
 *
 * @example
 * convertStringToByteArray("ab")
 * ==> [ 97, 98 ]
 * @internal
 * ```
 */
function generateKey(salt: string, randomString: string) {
  if (salt.length < 16) {
    salt = salt + '0000000000000000';
  }

  if (randomString.length < 16) {
    randomString = randomString + '0000000000000000';
  }

  salt = salt.substring(0, 16);
  randomString = randomString.substring(0, 16);

  const sigBytes = convertStringToByteArray(salt);
  const uuidBytes = convertStringToByteArray(randomString);
  const keyBytes: any = [];

  for (let i = 0; i < 16; ++i) {
    keyBytes[i * 2] = sigBytes[i];
    keyBytes[i * 2 + 1] = uuidBytes[i];
  }

  return convertByteArrToString(keyBytes);
}

//utitlity function
function byteAr16(byteArr: number[]) {
  return byteArr.concat(Array(16).fill(0)).slice(0, 16);
}

//utitlity function
function stringToBase64(str: string) {
  // Normally js has window.btoa to convert but we cant use it as window is not available

  const encryptedWord = Utf8Encoder.parse(str); // encryptedWord Array object

  const encrypted = Base64Encoder.stringify(encryptedWord);

  return encrypted;
}

/**
 * finalAesEncrypt
 *
 * @param token - The salt that will be used to generate the key
 * @param key - The salt that will be used to generate the key
 * @param randomString - The salt that will be used to generate the key
 *
 * ```
 */

function finalAesEncrypt(token: string, key: string, randomString: string) {
  // Do sha256 encoding of key and then convert to byte array and then take first 16 char
  const transformedKey = byteAr16(
    convertStringToByteArray(sha256Hash(key).toString())
  );
  const key1 = convertByteArrToString(transformedKey);
  const keyutf = Utf8Encoder.parse(key1); //turn base-64 string to UTF encoded wordarray object

  //encoding the data with Base64ENcoding hence the third param is true
  const ciphertext = encryptAesBase64(token, keyutf, {
    iv: keyutf,
    padding: NoPadding,
  }).data;

  return stringToBase64(randomString + '###' + ciphertext);
}

/**
 * generateChecksum
 *
 * @remarks
 * This service can be used to generate checksum .
 *
 * @param url - The url of the api which will have the generated checksum in the Headers
 * @param data - The payload data for the api. In case of get request it will be null
 * @param xReqId - The random request id associated with every request
 * @param useInternalSalt - This boolean value states whether internalSalt should be used for generating checksum.
 * @returns The final checksum that will be added in as request header in X-REQUEST-CHECKSUM
 * ```
 */
export function generateChecksum(url: string, data: any, xReqId: string) {
  const salt = getDomainConfig(window.location.hostname).checksumKey!;

  // Generate Actual token using url for API , payload data and xReqID
  const token = getActualToken(url, data, xReqId);

  const randomString = generateRandomString();

  // Calculate Key which will be used to encrypt
  const key = generateKey(salt, randomString);


  // Final encryption
  const finalStr = finalAesEncrypt(token, key, randomString);

  return finalStr;
}


export const isValidPayload = (payload: any) => {
  return typeof payload !== 'undefined' && !isEmpty(payload);
};

export const isUndefined = (payload: any) => {
  return typeof payload === 'undefined';
};
